'use strict';
const uuid = require('uuid');
const BigNumber = require('bignumber.js');
const _ = require('lodash');
const moment = require('moment');
const foldToAscii = require('diacritics').remove;
const extractPhone = require('./extractPhone');
const and = require('./and');
const or = require('./or');
const generatePaymentReference = require('./generatePaymentReference');
const generateBatchCode = require('./generateBatchCode');
const translateDonationAgreementTypes = require('./translateDonationAgreementType');
const translatePaymentFrequency = require('./translatePaymentFrequency');
const translateDonationAgreementPeriod = require('./translateDonationAgreementPeriod');
const translatePaymentMethod = require('./translatePaymentMethod');
const cleanseInfix = require('./cleanseInfix');
const cleanseInitials = require('./cleanseInitials');
const cleanseFirstName = require('./cleanseFirstName');
const cleanseLastName = require('./cleanseLastName');
const humanizeDuration = require('./humanizeDuration');
const formatAddress = require('./formatAddress');
const calculateAge = require('./calculateAge');
const toMapUri = require('./toMapUri');
const parseNewLinesForWord = require('./parseNewLinesForWord');
const numberToWords = require('./numberToWords');
const formatPostalCode = require('./formatPostalCode');
const getInitials = require('./getInitials');

moment.locale('nl');

const trimObject = function (obj) {
  if (!_.isObject(obj)) {
    return obj;
  }

  return _.transform(obj, function (result, value, key) {
    if (_.isString(value)) {
      result[key] = _.trim(value);
    } else if (_.isObject(value)) {
      result[key] = trimObject(value);
    } else {
      result[key] = value;
    }
  }, {});
};

const getNumberValue = function (text) {
  if (!_.isString(text)) {
    return undefined;
  }

  const isNegative = /^-/.test(text);

  text = text.replace(/^-/, '');

  if (/^0x/.test(text)) {
    return isNegative ? '-' + text : text;
  }

  if (!/^[0-9.,]+$/.test(text)) {
    return undefined;
  }

  const formatted = text.replace(/[.,]([0-9]{1,2})$/, '|$1');

  const cleaned = formatted.replace(/[.,]/g, '');

  const parsed = cleaned.replace('|', '.').replace(/^0+(.+)/, '$1').replace(/^\./, '0.');

  return isNegative ? '-' + parsed : parsed;
};

const parseAndGetBigNumber = function (text) {
  if (!_.isString(text)) {
    text = '' + text;
  }

  const parsed = getNumberValue(text);

  if (_.isNil(parsed)) {
    return undefined;
  }

  return new BigNumber(parsed);
};

const parseStrictAndGetBigNumber = function (text) {
  if (_.isNil(text)) {
    return undefined;
  }

  if (!_.isString(text)) {
    text = '' + text;
  }

  return new BigNumber(text);
};

const calculateYearlyAmount = function (amount, frequency) {
  return parseAndGetBigNumber(amount).times(parseAndGetBigNumber(frequency));
};

const calculateTotalAmount = function (amount, frequency, period) {
  const totalAmount = parseAndGetBigNumber(amount).times(parseAndGetBigNumber(frequency));
  let toNumber = parseAndGetBigNumber(period).toNumber();

  toNumber = toNumber === 0 ? 0 : toNumber / 12;

  return totalAmount.times(new BigNumber(toNumber));
};

const calculateEndDate = function (startDate, period) {
  const asIsoMoment = moment(startDate, [moment.ISO_8601, 'l']);
  if (asIsoMoment.isValid()) {
    const formattedDate = asIsoMoment.format('DD-MM-YYYY');
    return moment(formattedDate, 'DD-MM-YYYY').add(period, 'months').format('DD-MM-YYYY');
  } else {
    return '-';
  }
};

const calculateDuration = function (startDate, endDate) {
  const start = moment(startDate, [moment.ISO_8601, 'l'], true);
  const end = moment(endDate, [moment.ISO_8601, 'l'], true);

  let yearDif = end.diff(start, 'y', true);

  // Test for edge case
  const javascriptFebruary = 1;

  if (start.date() === 29 && start.month() === javascriptFebruary && end.date() === 28 && end.month() === javascriptFebruary) {
    yearDif = Math.ceil(yearDif);
  }

  const years = Math.floor(yearDif);
  let months = 0;
  let days = 0;

  if (yearDif % 1 > 0) {
    end.add(-years, 'y');

    let monthDif = end.diff(start, 'M', true);

    // added extra checks for ranges that start on the last day of the month
    if (start.date() === 31 && end.date() === 30) {
      months = Math.ceil(monthDif);
    } else if ((start.date() === 29 || start.date() === 30 | start.date() === 31) && ((end.date() === 27 || end.date() === 28 || end.date() === 29) && end.month() === javascriptFebruary)) {
      months = Math.ceil(monthDif);
    } else {
      months = Math.floor(monthDif);
    }

    if (monthDif % 1 > 0) {
      end.add(-months, 'M');

      days = end.diff(start, 'd');
    }
  }

  const parts = [];

  if (years > 0) {
    parts.push(years + ' jaar');
  }

  if (months > 0) {
    if (months > 1) {
      parts.push(months + ' maanden');
    } else {
      parts.push(months + ' maand');
    }
  }

  if (days > 0) {
    if (days > 1) {
      parts.push(days + ' dagen');
    } else {
      parts.push(days + ' dag');
    }
  }

  return parts.join(' en ');
};

const flattenEntity = function (doc) {
  if (_.isPlainObject(doc)) {
    for (const prop in doc) {
      if (prop === 'meta' && !_.isEmpty(doc.meta)) {
        if (!_.isNil(doc.meta.key)) {
          doc.key = doc.meta.key;
        }
        if (!_.isNil(doc.meta.entity)) {
          doc.entity = doc.meta.entity;
        }
        if (!_.isNil(doc.meta.revision)) {
          doc.revision = doc.meta.revision;
        }
        delete doc.meta;
      } else {
        doc[prop] = flattenEntity(doc[prop]);
      }
    }
  }

  return doc;
};

module.exports = {
  parseAndGetBigNumber: parseAndGetBigNumber,
  parseStrictAndGetBigNumber: parseStrictAndGetBigNumber,
  getNumberValue: getNumberValue,
  formatContactName: function (initials, firstName, infix, lastName) {
    if (_.isNil(lastName)) {
      return;
    }

    const rest = [firstName || initials, infix]
      .filter((namePart) => {
        return !_.isEmpty(namePart);
      });

    if (_.size(rest) === 0) {
      return lastName;
    }

    return lastName + ', ' + rest.join(' ');
  },
  formatVolunteerName: function (initials, firstName, infix, lastName) {
    if (_.isNil(lastName)) {
      return;
    }

    const first = initials || firstName;
    const names = [];

    if (_.isString(first) && !_.isEmpty(first.trim())) {
      names.push(first);
    }

    if (_.isString(infix) && !_.isEmpty(infix.trim())) {
      names.push(infix);
    }

    if (_.isString(firstName) && !_.isEmpty(initials) && !_.isEmpty(firstName.trim())) {
      names.push('(' + firstName + ')');
    }

    if (_.size(names) === 0) {
      return lastName;
    }

    return lastName + ', ' + names.join(' ');
  },
  formatFullName: function (initials, firstName, infix, lastName) {
    const first = firstName || initials;
    const names = [];

    if (_.isString(first) && !_.isEmpty(first.trim())) {
      names.push(first);
    }

    if (_.isString(infix) && !_.isEmpty(infix.trim())) {
      names.push(infix);
    }

    if (_.isString(lastName) && !_.isEmpty(lastName.trim())) {
      names.push(lastName);
    }

    return names.join(' ');
  },
  formatFullNameWithInitials: function (initials, firstName, infix, lastName) {
    const first = initials || firstName;
    const names = [];

    if (_.isString(first) && !_.isEmpty(first.trim())) {
      names.push(first);
    }

    if (_.isString(infix) && !_.isEmpty(infix.trim())) {
      names.push(infix);
    }

    if (_.isString(lastName) && !_.isEmpty(lastName.trim())) {
      names.push(lastName);
    }

    if (_.isString(firstName) && !_.isEmpty(initials) && !_.isEmpty(firstName.trim())) {
      names.push('(' + firstName + ')');
    }

    return names.join(' ');
  },
  trimObject: trimObject,
  uuid: function () {
    return uuid.v4();
  },
  calculateYearlyAmount: calculateYearlyAmount,
  calculateTotalAmount: calculateTotalAmount,
  calculateEndDate: calculateEndDate,
  calculateDuration: calculateDuration,
  flattenEntity: flattenEntity,
  sepaFold: function (string) {
    return foldToAscii(string).replace(/[^\\A-Z0-9\/\-\?\:\(\)\.\,\'\+\s]|\\n/gi, '');
  },
  extractPhone: extractPhone,
  and: and,
  or: or,
  generatePaymentReference: generatePaymentReference,
  generateBatchCode: generateBatchCode,
  translateDonationAgreementTypes: translateDonationAgreementTypes,
  translatePaymentFrequency: translatePaymentFrequency,
  translateDonationAgreementPeriod: translateDonationAgreementPeriod,
  translatePaymentMethod: translatePaymentMethod,
  cleanseInitials: cleanseInitials,
  cleanseInfix: cleanseInfix,
  cleanseFirstName: cleanseFirstName,
  cleanseLastName: cleanseLastName,
  humanizeDuration: humanizeDuration,
  formatAddress: formatAddress,
  calculateAge: calculateAge,
  toMapUri: toMapUri,
  parseNewLinesForWord: parseNewLinesForWord,
  numberToWords: numberToWords,
  formatPostalCode,
  getInitials
};
